import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { leagueParameterColumns } from "../helpers/teamLeagueColumns";
import { Button } from "@mui/material";
import WorkspaceStandingsTable from "./WorkspaceStandingsTable";
import { prepareLeagueRowsForScope } from "../helpers/compactLeagueRows";
import { futureSummary } from "../helpers/teamAnalysis";
export default function TeamLeagueInline({
  data,
  teamId,
  en,
  mode,
  actualOnly = false,
}) {
  const { i18n } = useTranslation();
  const [chosen, setChosen] = useState([]),
    [hidden, setHidden] = useState([]),
    [venue, setVenue] = useState("overall");
  const catalog = leagueParameterColumns(
    data,
    en,
    (key) =>
      i18n.t(
        "help.automation.business.statisticNames." +
          data.state?.registry?.[key]?.title,
        { defaultValue: data.state?.registry?.[key]?.title || key }
      ),
    { venue }
  );
  const [showStatistics, setShowStatistics] = useState(false),
    [source, setSource] = useState("actual");
  const t = (d, e) => (en ? e : d);
  const fmt = (n, d = 2) =>
    Number.isFinite(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : "—";
  const labels = {
    played: t("Spiele", "Played"),
    won: t("Siege", "Wins"),
    draw: t("Remis", "Draws"),
    lost: t("Niederlagen", "Losses"),
    goals: t("Tore", "Goals"),
    difference: t("Differenz", "Difference"),
    points: t("Punkte", "Points"),
  };
  const names = {
    actual: t("Real erspielt", "Actual performance"),
    expected: t("Ergebnismodell · erwartet", "Results model · expected"),
    should: "Should Goals",
    market: t("Historische Marktrückrechnung", "Historical market inversion"),
  };
  const rows = prepareLeagueRowsForScope(
    source === "actual" ? data.actual_table : data.comparison_tables?.[source],
    source,
    venue
  ).map((r) => ({
    ...r,
    result: futureSummary(data.result_teams?.[r.teamId]),
    statistics: data.ranking?.find((x) => x.team_id === r.teamId),
  }));
  const rulesFor = (id) =>
    (data.standing_rules || []).filter(
      (r) =>
        r.is_regular_stage !== false &&
        (r.position != null
          ? Number(r.position) === rows.find((x) => x.teamId === id)?.position
          : Number(r.team_id) === id)
    );
  return (
    <div className={`team-league-workspace tl-${mode}`}>
      <label>
        {t("Spielort", "Venue")}{" "}
        <select value={venue} onChange={(e) => setVenue(e.target.value)}>
          <option value="overall">{t("Gesamt", "Overall")}</option>
          <option value="home">{t("Heim", "Home")}</option>
          <option value="away">{t("Auswärts", "Away")}</option>
        </select>
      </label>
      {!actualOnly && (
        <label>
          {t("Tabellengrundlage", "Table basis")}{" "}
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {Object.entries(names).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
      )}
      {!actualOnly && (
        <Button onClick={() => setShowStatistics((v) => !v)}>
          {showStatistics
            ? t("Statistik ausblenden", "Hide statistics")
            : t("Statistik anzeigen", "Show statistics")}
        </Button>
      )}
      <details>
        <summary>{t("Spalten auswählen", "Choose columns")}</summary>
        {Object.entries(labels).map(([k, label]) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={!hidden.includes(k)}
              onChange={(e) =>
                setHidden(
                  e.target.checked
                    ? hidden.filter((x) => x !== k)
                    : [...hidden, k]
                )
              }
            />
            {label}{" "}
          </label>
        ))}
        {catalog.map((c) => (
          <label key={c.key}>
            <input
              type="checkbox"
              checked={chosen.includes(c.key)}
              onChange={(e) =>
                setChosen(
                  e.target.checked
                    ? [...chosen, c.key]
                    : chosen.filter((x) => x !== c.key)
                )
              }
            />
            {c.label}{" "}
          </label>
        ))}
      </details>
      <WorkspaceStandingsTable
        {...{ rows, data, showStatistics, rulesFor, fmt, t, en }}
        selectedTeam={teamId}
        visibleColumns={Object.keys(labels).filter((k) => !hidden.includes(k))}
        extraColumns={catalog.filter((c) => chosen.includes(c.key))}
        visibleMetrics={["quotient", "goalAverage"]}
        groups={[
          names[source],
          t(
            "Gespeicherte Settings · Ergebnismodell",
            "Saved settings · Results model"
          ),
          t(
            "Gespeicherte Settings · Statistikmodell",
            "Saved settings · Statistics model"
          ),
        ]}
        columnClass={(k) =>
          ["played", "won", "draw", "lost"].includes(k)
            ? "tl-record-column"
            : k === "points"
            ? "tl-points"
            : k === "goals"
            ? "tl-goals"
            : "tl-number"
        }
        digits={(k) => (k === "played" || source === "actual" ? 0 : 2)}
        labels={labels}
      />
    </div>
  );
}
